import React, { useEffect, useState } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useParams } from "react-router-dom";
import { fetchReportById } from "./progressReportApi";
import { toDateMaybe } from "../tutor/session_logs/sessionLogApi";
import "./ProgressReportDetailPage.css";

// ─── constants ────────────────────────────────────────────────────────────────

const PIE_COLORS = [
  "#0f172a",
  "#334155",
  "#475569",
  "#64748b",
  "#94a3b8",
  "#cbd5e1",
  "#e2e8f0",
];

// ─── helpers ──────────────────────────────────────────────────────────────────

function formatDate(value) {
  if (!value) return "—";
  if (typeof value === "string" && value.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [y, m, d] = value.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString([], {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }
  const d = toDateMaybe(value);
  if (!d) return String(value);
  return d.toLocaleDateString([], {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function riskPillClass(level) {
  const l = String(level || "").toLowerCase();
  if (l === "on track") return "prd-riskPill prd-riskPill--ontrack";
  if (l.includes("needs")) return "prd-riskPill prd-riskPill--needs";
  if (l.includes("risk")) return "prd-riskPill prd-riskPill--atrisk";
  return "prd-riskPill prd-riskPill--ontrack";
}

function ratingBarWidth(value, max = 5) {
  if (value === null || value === undefined) return "0%";
  return `${Math.min(100, Math.round((value / max) * 100))}%`;
}

function fmtHours(h) {
  return `${Number(h || 0).toFixed(1)} hrs`;
}

function fmtPct(n) {
  if (n === null || n === undefined) return "N/A";
  return `${n}%`;
}

// ─── sub-components ───────────────────────────────────────────────────────────

function HoursSummary({ normalizedHours, totalHours }) {
  const entries = Object.entries(normalizedHours || {})
    .filter(([, h]) => Number(h) > 0)
    .sort(([, a], [, b]) => Number(b) - Number(a));

  if (!entries.length) {
    return <p className="prd-narrative">No session hours recorded in this period.</p>;
  }

  const total = entries.reduce((s, [, h]) => s + Number(h), 0);

  const pieData = entries.map(([name, value]) => ({
    name,
    value: Math.round(Number(value) * 10) / 10,
  }));

  return (
    <div className="prd-hoursLayout">
      {/* List */}
      <div>
        <ul className="prd-hoursList">
          {entries.map(([subject, hours], idx) => {
            const pct = total > 0 ? Math.round((Number(hours) / total) * 100) : 0;
            return (
              <li key={subject} className="prd-hoursItem">
                <div className="prd-hoursSubject">
                  <span
                    className="prd-colorDot"
                    style={{ background: PIE_COLORS[idx % PIE_COLORS.length] }}
                  />
                  {subject}
                </div>
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <span className="prd-hoursPct">{pct}%</span>
                  <span className="prd-hoursValue">{fmtHours(hours)}</span>
                </div>
              </li>
            );
          })}
        </ul>

        <div className="prd-hoursTotal">
          <span>Total Hours</span>
          <span>{fmtHours(totalHours || total)}</span>
        </div>
      </div>

      {/* Pie chart */}
      <div style={{ minHeight: 240 }}>
        <ResponsiveContainer width="100%" height={240}>
          <PieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="45%"
              outerRadius={88}
              innerRadius={44}
              dataKey="value"
              paddingAngle={2}
            >
              {pieData.map((entry, idx) => (
                <Cell
                  key={entry.name}
                  fill={PIE_COLORS[idx % PIE_COLORS.length]}
                />
              ))}
            </Pie>
            <Tooltip
              formatter={(value, name) => [fmtHours(value), name]}
              contentStyle={{
                borderRadius: 8,
                border: "1px solid #e2e8f0",
                fontSize: 12,
              }}
            />
            <Legend
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function AttendanceSection({ metrics }) {
  const {
    totalSessions = 0,
    homeworkCompletionRate,
    homeworkCompleted = 0,
    homeworkPartial = 0,
    homeworkNotDone = 0,
  } = metrics || {};

  return (
    <div className="prd-twoCol">
      <div>
        <p className="prd-sectionLabel">Attendance</p>
        <div className="prd-statGrid">
          <div className="prd-statCard">
            <div className="prd-statValue">{totalSessions}</div>
            <div className="prd-statLabel">Total Sessions</div>
          </div>
        </div>
      </div>

      <div>
        <p className="prd-sectionLabel">Homework</p>
        <div className="prd-statGrid">
          <div className="prd-statCard">
            <div className="prd-statValue">{fmtPct(homeworkCompletionRate)}</div>
            <div className="prd-statLabel">Completion Rate</div>
          </div>
          <div className="prd-statCard">
            <div className="prd-statValue">{homeworkCompleted}</div>
            <div className="prd-statLabel">Completed</div>
          </div>
          <div className="prd-statCard">
            <div className="prd-statValue">{homeworkPartial}</div>
            <div className="prd-statLabel">Partial</div>
          </div>
          <div className="prd-statCard">
            <div className="prd-statValue">{homeworkNotDone}</div>
            <div className="prd-statLabel">Not Done</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function LearningHabitsSection({ metrics }) {
  const ratings = [
    { key: "avgEffort", label: "Effort" },
    { key: "avgMotivation", label: "Motivation" },
    { key: "avgBehavior", label: "Behavior" },
    { key: "avgFocus", label: "Focus" },
    { key: "avgConfidence", label: "Confidence" },
  ];

  const hasAny = ratings.some((r) => metrics?.[r.key] !== null && metrics?.[r.key] !== undefined);
  if (!hasAny) {
    return (
      <p className="prd-narrative">
        Rating data was not recorded for sessions in this period.
      </p>
    );
  }

  return (
    <div>
      {ratings.map(({ key, label }) => {
        const val = metrics?.[key];
        if (val === null || val === undefined) return null;
        return (
          <div key={key} className="prd-ratingRow">
            <div className="prd-ratingLabel">{label}</div>
            <div className="prd-ratingBarOuter">
              <div
                className="prd-ratingBarInner"
                style={{ width: ratingBarWidth(val) }}
              />
            </div>
            <div className="prd-ratingScore">{Number(val).toFixed(1)}</div>
          </div>
        );
      })}
    </div>
  );
}

function NarrativeSection({ text, fallback }) {
  const str = String(text || "").trim();
  if (!str) return <p className="prd-narrative">{fallback || "Information not available."}</p>;
  return <p className="prd-narrative">{str}</p>;
}

function BulletSection({ items, fallback }) {
  const list = Array.isArray(items)
    ? items.map((i) => String(i || "").trim()).filter(Boolean)
    : [];
  if (!list.length) {
    return <p className="prd-narrative">{fallback || "No items available."}</p>;
  }
  return (
    <ul className="prd-bulletList">
      {list.map((item, idx) => (
        // eslint-disable-next-line react/no-array-index-key
        <li key={idx}>{item}</li>
      ))}
    </ul>
  );
}

function GoalsList({ items }) {
  const list = Array.isArray(items)
    ? items.map((i) => String(i || "").trim()).filter(Boolean)
    : [];
  if (!list.length) return null;
  return (
    <ol className="prd-goalsList">
      {list.map((item, idx) => (
        // eslint-disable-next-line react/no-array-index-key
        <li key={idx}>
          <span className="prd-goalNum">{idx + 1}</span>
          <span>{item}</span>
        </li>
      ))}
    </ol>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────────

export default function ProgressReportDetailPage() {
  const { reportId } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!reportId) {
      setError("No report ID found in the URL.");
      setLoading(false);
      return;
    }

    let alive = true;
    fetchReportById(reportId)
      .then((doc) => {
        if (!alive) return;
        if (!doc) {
          setError("Report not found. It may have been deleted or the link is invalid.");
        } else {
          setReport(doc);
        }
      })
      .catch((err) => {
        console.error("Failed to load report", err);
        if (alive) setError("Failed to load the report. Please try again.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => { alive = false; };
  }, [reportId]);

  if (loading) {
    return (
      <div className="prd-loadingShell">
        <div className="prd-spinner" />
        Loading report…
      </div>
    );
  }

  if (error) {
    return (
      <div className="prd-errorShell">
        <div>
          <strong>Unable to load report</strong>
          <br />
          {error}
        </div>
      </div>
    );
  }

  if (!report) return null;

  const { metrics = {}, ai = {}, normalizedHours = {} } = report;
  const riskLevel = ai.riskLevel || metrics.riskLevel || "On Track";
  const generatedAt = report.generatedAt
    ? (() => {
        const d = toDateMaybe(report.generatedAt);
        return d
          ? d.toLocaleDateString([], {
              month: "long",
              day: "numeric",
              year: "numeric",
            })
          : "—";
      })()
    : "—";

  return (
    <div className="prd-outer">
      {/* Top action bar */}
      <div className="prd-topBar">
        <button
          type="button"
          className="prd-backBtn"
          onClick={() => window.close()}
        >
          ← Close
        </button>
        <button
          type="button"
          className="prd-printBtn"
          onClick={() => window.print()}
        >
          Print / Save PDF
        </button>
      </div>

      <div className="prd-report">
        {/* ── Report Header ── */}
        <div className="prd-header">
          <div className="prd-headerTop">
            <div>
              <div className="prd-brandName">True Education Cumming</div>
              <div className="prd-reportTitle">
                Student Progress Report
              </div>
              <div className="prd-reportPeriod">
                {formatDate(report.startDate)} – {formatDate(report.endDate)}
              </div>
            </div>
            <span className={riskPillClass(riskLevel)}>{riskLevel}</span>
          </div>

          <div className="prd-headerMeta">
            <div className="prd-metaItem">
              <label>Student</label>
              <div className="prd-metaValue">{report.studentName || "—"}</div>
            </div>
            <div className="prd-metaItem">
              <label>Sessions</label>
              <div className="prd-metaValue">{report.sessionCount ?? "—"}</div>
            </div>
            <div className="prd-metaItem">
              <label>Total Hours</label>
              <div className="prd-metaValue">
                {fmtHours(metrics.totalHours)}
              </div>
            </div>
            <div className="prd-metaItem">
              <label>Report Date</label>
              <div className="prd-metaValue">{generatedAt}</div>
            </div>
            <div className="prd-metaItem">
              <label>Tutor(s)</label>
              <div className="prd-metaValue">
                {(report.tutors || []).slice(0, 2).join(", ") || "—"}
              </div>
            </div>
          </div>
        </div>

        <div className="prd-body">
          {/* ── Overall Progress ── */}
          {ai.overallProgress && (
            <div className="prd-section">
              <p className="prd-sectionLabel">Summary</p>
              <NarrativeSection text={ai.overallProgress} />
            </div>
          )}

          {/* ── Hours Summary ── */}
          <div className="prd-section">
            <p className="prd-sectionLabel">Hours Summary</p>
            <HoursSummary
              normalizedHours={normalizedHours}
              totalHours={metrics.totalHours}
            />
          </div>

          {/* ── Attendance & Homework (side-by-side stats) ── */}
          <div className="prd-section">
            <AttendanceSection metrics={metrics} />
          </div>

          {/* ── Academic Progress ── */}
          <div className="prd-section">
            <p className="prd-sectionLabel">Academic Progress</p>
            <NarrativeSection
              text={ai.academicProgress}
              fallback="Academic progress information will appear here after generating the report with AI analysis."
            />
          </div>

          {/* ── Class Performance & Homework Analysis (2-col) ── */}
          <div className="prd-section">
            <div className="prd-twoCol">
              <div>
                <p className="prd-sectionLabel">Class Performance</p>
                <NarrativeSection
                  text={ai.classPerformance}
                  fallback="Performance data not available."
                />
              </div>
              <div>
                <p className="prd-sectionLabel">Homework Analysis</p>
                <NarrativeSection
                  text={ai.homeworkAnalysis}
                  fallback="Homework analysis not available."
                />
              </div>
            </div>
          </div>

          {/* ── Learning Habits (ratings + narrative) ── */}
          <div className="prd-section">
            <p className="prd-sectionLabel">Learning Habits & Engagement</p>
            <div className="prd-twoCol">
              <LearningHabitsSection metrics={metrics} />
              <div>
                <NarrativeSection
                  text={ai.learningHabitsNarrative}
                  fallback="Learning habits narrative not available."
                />
              </div>
            </div>
          </div>

          {/* ── Strengths & Areas for Improvement ── */}
          <div className="prd-section">
            <div className="prd-bulletCompare">
              <div className="prd-bulletCol">
                <h4>Key Strengths</h4>
                <BulletSection
                  items={ai.keyStrengths}
                  fallback="Strengths will be identified in the AI-generated report."
                />
              </div>
              <div className="prd-bulletCol">
                <h4>Areas for Improvement</h4>
                <BulletSection
                  items={ai.areasForImprovement}
                  fallback="Improvement areas will be identified in the AI-generated report."
                />
              </div>
            </div>
          </div>

          {/* ── Instructor Comments ── */}
          {ai.instructorComments && (
            <div className="prd-section">
              <p className="prd-sectionLabel">Instructor Comments</p>
              <div className="prd-commentBlock">
                <p className="prd-commentText">{ai.instructorComments}</p>
              </div>
            </div>
          )}

          {/* ── Goals & Action Plan ── */}
          {Array.isArray(ai.goalsAndActionPlan) && ai.goalsAndActionPlan.length > 0 && (
            <div className="prd-section">
              <p className="prd-sectionLabel">Goals & Action Plan</p>
              <GoalsList items={ai.goalsAndActionPlan} />
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="prd-footer">
          <div className="prd-footerBrand">True Education Cumming</div>
          <div className="prd-footerMeta">
            Report ID: {report.id || report.reportId} · Generated {generatedAt}
          </div>
        </div>
      </div>
    </div>
  );
}
